// Yard auto-pack: lays UNPLACED panels onto the yard tables in neat rows.
// - respects panels already on tables (new panels start below existing content per table)
// - groups by concrete strength (28-day MPa), largest-first within a group
// - rotates a panel 90° when that helps it fit the current row
// - keeps `gap` metres of clearance between panels
// Returns { placements: [{panelId, table, x, y, rotated}], unfit: <count> }

function orientations(panel, allowRotate) {
  // natural: horizontal extent = length, vertical extent = width (matches YardPlanner dims)
  const nat = { w: panel.length, h: panel.width, rot: false };
  const rot = { w: panel.width, h: panel.length, rot: true };
  return allowRotate ? [nat, rot] : [nat];
}

export function packYard({ tables, panels, placements, panelsById, gap = 0.6, allowRotate = true }) {
  const validTables = (tables || []).filter((t) => t.w > 0 && t.h > 0);
  const onTable = new Set((placements || []).map((p) => p.panelId));

  let queue = (panels || []).filter(
    (p) => !onTable.has(p.id) && p.width > 0 && p.length > 0
  );
  // group by strength (desc), then largest area first for tighter rows
  queue = queue.slice().sort(
    (a, b) => (b.strength28 || 0) - (a.strength28 || 0) || (b.area || 0) - (a.area || 0)
  );

  // starting y per table = below any existing content (no overlap with placed panels)
  const baseY = {};
  validTables.forEach((t) => {
    let maxBottom = 0;
    (placements || [])
      .filter((pl) => pl.table === t.id)
      .forEach((pl) => {
        const panel = panelsById[pl.panelId];
        if (!panel) return;
        const vExt = pl.rotated ? panel.length : panel.width;
        maxBottom = Math.max(maxBottom, pl.y + vExt);
      });
    baseY[t.id] = maxBottom > 0 ? maxBottom + gap : 0;
  });

  const result = [];
  let unfit = 0;

  if (!validTables.length) return { placements: result, unfit: queue.length };

  // Per-table row cursor so a large panel that fills one table never blocks
  // smaller panels from using leftover space on other tables.
  const cursor = {};
  validTables.forEach((t) => {
    cursor[t.id] = { cx: 0, cy: baseY[t.id], rowH: 0 };
  });

  const tryPlace = (panel, t) => {
    const st = cursor[t.id];
    const cands = orientations(panel, allowRotate);
    while (true) {
      const choice = cands.find((c) => st.cx + c.w <= t.w + 1e-9 && st.cy + c.h <= t.h + 1e-9);
      if (choice) {
        const placement = {
          panelId: panel.id,
          table: t.id,
          x: +st.cx.toFixed(3),
          y: +st.cy.toFixed(3),
          rotated: choice.rot,
        };
        st.cx += choice.w + gap;
        st.rowH = Math.max(st.rowH, choice.h);
        return placement;
      }
      if (st.cx > 0) {
        // wrap to a new row on this table
        st.cy += st.rowH + gap;
        st.cx = 0;
        st.rowH = 0;
        if (st.cy >= t.h) return null; // table full vertically
      } else {
        return null; // empty row and still doesn't fit -> try next table
      }
    }
  };

  for (const panel of queue) {
    let placed = false;
    for (const t of validTables) {
      const placement = tryPlace(panel, t);
      if (placement) {
        result.push(placement);
        placed = true;
        break;
      }
    }
    if (!placed) unfit += 1;
  }

  return { placements: result, unfit };
}
